/**
 * Resident Profile Service
 * Centralised CRUD for medication edits, document management,
 * and profile field updates — persisted to localStorage.
 */

import LocalStorageService from '../storage/LocalStorageService';
import { logDataModification } from '../../utils/auditLogger';

const MED_KEY = 'resident_medications';
const DOC_KEY = 'resident_documents';
const PROFILE_EDITS_KEY = 'resident_profile_edits';

class ResidentProfileService {
  constructor() {
    [MED_KEY, DOC_KEY, PROFILE_EDITS_KEY].forEach((k) => {
      if (!LocalStorageService.get(k)) LocalStorageService.set(k, {});
    });
  }

  _id() {
    return `RP-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  _getMap(key, residentId) {
    const all = LocalStorageService.get(key, {});
    return all[residentId] || null;
  }

  _setMap(key, residentId, value) {
    const all = LocalStorageService.get(key, {});
    all[residentId] = value;
    LocalStorageService.set(key, all);
  }

  // ── Medications ────────────────────────────────────────────────────────

  initialiseMedications(residentId, baseMedications) {
    if (this._getMap(MED_KEY, residentId)) return this._getMap(MED_KEY, residentId);
    const seed = {
      current: (baseMedications || []).map((m, i) => ({
        ...m,
        id: m.id || `MED-seed-${i}`,
        refillHistory: m.refillHistory || [],
        scheduleHistory: m.scheduleHistory || [],
      })),
      discontinued: [],
      updatedAt: new Date().toISOString(),
    };
    this._setMap(MED_KEY, residentId, seed);
    return seed;
  }

  getMedications(residentId) {
    return this._getMap(MED_KEY, residentId);
  }

  saveMedication(residentId, medicationData, userId = 'system') {
    const store = this._getMap(MED_KEY, residentId);
    if (!store) return { success: false, error: 'Medications not initialised' };

    const idx = store.current.findIndex((m) => m.id === medicationData.id);
    if (idx === -1) return { success: false, error: 'Medication not found' };

    const prev = { ...store.current[idx] };
    store.current[idx] = {
      ...store.current[idx],
      ...medicationData,
      updatedAt: new Date().toISOString(),
      updatedBy: userId,
    };

    this._setMap(MED_KEY, residentId, { ...store, updatedAt: new Date().toISOString() });
    logDataModification('medication', medicationData.id, 'UPDATE', prev, store.current[idx], userId);
    return { success: true, data: store.current[idx] };
  }

  processRefill(residentId, medicationId, refillData, userId = 'system') {
    const store = this._getMap(MED_KEY, residentId);
    if (!store) return { success: false, error: 'Medications not initialised' };

    const idx = store.current.findIndex((m) => m.id === medicationId);
    if (idx === -1) return { success: false, error: 'Medication not found' };

    const refillRecord = {
      id: this._id(),
      date: new Date().toISOString(),
      quantity: refillData.quantity || 'Standard',
      pharmacy: refillData.pharmacy || 'Default Pharmacy',
      authorisedBy: userId,
      notes: refillData.notes || '',
    };

    store.current[idx].refillHistory = store.current[idx].refillHistory || [];
    store.current[idx].refillHistory.unshift(refillRecord);
    store.current[idx].lastRefill = refillRecord.date;
    store.current[idx].updatedAt = new Date().toISOString();

    this._setMap(MED_KEY, residentId, { ...store, updatedAt: new Date().toISOString() });
    logDataModification('medication_refill', refillRecord.id, 'CREATE', null, refillRecord, userId);
    return { success: true, data: refillRecord };
  }

  updateSchedule(residentId, medicationId, scheduleData, userId = 'system') {
    const store = this._getMap(MED_KEY, residentId);
    if (!store) return { success: false, error: 'Medications not initialised' };

    const idx = store.current.findIndex((m) => m.id === medicationId);
    if (idx === -1) return { success: false, error: 'Medication not found' };

    const prev = store.current[idx].schedule;
    store.current[idx].schedule = scheduleData.schedule || store.current[idx].schedule;
    store.current[idx].frequency = scheduleData.frequency || store.current[idx].frequency;
    store.current[idx].scheduleHistory = store.current[idx].scheduleHistory || [];
    store.current[idx].scheduleHistory.unshift({
      id: this._id(),
      date: new Date().toISOString(),
      previousSchedule: prev,
      newSchedule: scheduleData.schedule,
      changedBy: userId,
      reason: scheduleData.reason || '',
    });
    store.current[idx].updatedAt = new Date().toISOString();

    this._setMap(MED_KEY, residentId, { ...store, updatedAt: new Date().toISOString() });
    logDataModification('medication_schedule', medicationId, 'UPDATE', prev, scheduleData, userId);
    return { success: true, data: store.current[idx] };
  }

  addMedication(residentId, medData, userId = 'system') {
    const store = this._getMap(MED_KEY, residentId);
    if (!store) {
      const seed = this.initialiseMedications(residentId, []);
      return this.addMedication(residentId, medData, userId);
    }

    const med = {
      id: this._id(),
      ...medData,
      refillHistory: [],
      scheduleHistory: [],
      createdAt: new Date().toISOString(),
      createdBy: userId,
    };

    store.current.push(med);
    store.updatedAt = new Date().toISOString();
    this._setMap(MED_KEY, residentId, store);
    logDataModification('medication', med.id, { action: { old: null, new: 'CREATE' } }, userId);
    return { success: true, data: med };
  }

  discontinueMedication(residentId, medId, reason, userId = 'system') {
    const store = this._getMap(MED_KEY, residentId);
    if (!store) return { success: false, error: 'Medications not initialised' };

    const idx = store.current.findIndex((m) => m.id === medId);
    if (idx === -1) return { success: false, error: 'Medication not found' };

    const med = store.current.splice(idx, 1)[0];
    med.status = 'discontinued';
    med.discontinuedAt = new Date().toISOString();
    med.discontinuedBy = userId;
    med.discontinueReason = reason || '';

    store.discontinued = store.discontinued || [];
    store.discontinued.push(med);
    store.updatedAt = new Date().toISOString();
    this._setMap(MED_KEY, residentId, store);
    logDataModification('medication', medId, { status: { old: 'active', new: 'discontinued' } }, userId);
    return { success: true, data: med };
  }

  deleteMedication(residentId, medId, userId = 'system') {
    const store = this._getMap(MED_KEY, residentId);
    if (!store) return { success: false, error: 'Medications not initialised' };

    const idx = store.current.findIndex((m) => m.id === medId);
    if (idx === -1) return { success: false, error: 'Medication not found in current list' };

    const removed = store.current.splice(idx, 1)[0];
    store.updatedAt = new Date().toISOString();
    this._setMap(MED_KEY, residentId, store);
    logDataModification('medication', medId, { action: { old: 'active', new: 'DELETED' } }, userId);
    return { success: true, data: removed };
  }

  // ── Documents ──────────────────────────────────────────────────────────

  initialiseDocuments(residentId, baseDocs) {
    if (this._getMap(DOC_KEY, residentId)) return this._getMap(DOC_KEY, residentId);
    const seed = {
      documents: (baseDocs || []).map((d, i) => ({
        ...d,
        id: d.id || `DOC-seed-${i}`,
        downloads: 0,
      })),
      updatedAt: new Date().toISOString(),
    };
    this._setMap(DOC_KEY, residentId, seed);
    return seed;
  }

  getDocuments(residentId) {
    return this._getMap(DOC_KEY, residentId);
  }

  addDocument(residentId, docData, userId = 'system') {
    const store = this._getMap(DOC_KEY, residentId) || { documents: [], updatedAt: new Date().toISOString() };

    const doc = {
      id: this._id(),
      name: docData.name,
      type: docData.type || 'General',
      category: docData.category || 'General',
      description: docData.description || '',
      date: docData.date || new Date().toISOString().split('T')[0],
      uploadedBy: userId,
      size: docData.size || '—',
      status: docData.status || 'Active',
      downloads: 0,
      createdAt: new Date().toISOString(),
    };

    store.documents.unshift(doc);
    store.updatedAt = new Date().toISOString();
    this._setMap(DOC_KEY, residentId, store);
    logDataModification('document', doc.id, 'CREATE', null, doc, userId);
    return { success: true, data: doc };
  }

  recordDownload(residentId, docId, userId = 'system') {
    const store = this._getMap(DOC_KEY, residentId);
    if (!store) return { success: false, error: 'Documents not initialised' };

    const idx = store.documents.findIndex((d) => d.id === docId);
    if (idx === -1) return { success: false, error: 'Document not found' };

    store.documents[idx].downloads = (store.documents[idx].downloads || 0) + 1;
    store.documents[idx].lastDownloadedAt = new Date().toISOString();
    store.documents[idx].lastDownloadedBy = userId;
    this._setMap(DOC_KEY, residentId, store);
    logDataModification('document_download', docId, 'ACCESS', null, { userId, docId }, userId);
    return { success: true, data: store.documents[idx] };
  }

  deleteDocument(residentId, docId, userId = 'system') {
    const store = this._getMap(DOC_KEY, residentId);
    if (!store) return { success: false, error: 'Documents not initialised' };

    const idx = store.documents.findIndex((d) => d.id === docId);
    if (idx === -1) return { success: false, error: 'Document not found' };

    const removed = store.documents.splice(idx, 1)[0];
    store.updatedAt = new Date().toISOString();
    this._setMap(DOC_KEY, residentId, store);
    logDataModification('document', docId, { action: { old: 'active', new: 'DELETED' } }, userId);
    return { success: true, data: removed };
  }

  updateDocument(residentId, docId, updates, userId = 'system') {
    const store = this._getMap(DOC_KEY, residentId);
    if (!store) return { success: false, error: 'Documents not initialised' };

    const idx = store.documents.findIndex((d) => d.id === docId);
    if (idx === -1) return { success: false, error: 'Document not found' };

    const prev = { ...store.documents[idx] };
    store.documents[idx] = { ...store.documents[idx], ...updates, updatedAt: new Date().toISOString() };
    store.updatedAt = new Date().toISOString();
    this._setMap(DOC_KEY, residentId, store);
    logDataModification('document', docId, { name: { old: prev.name, new: store.documents[idx].name } }, userId);
    return { success: true, data: store.documents[idx] };
  }

  // ── Profile Edits ──────────────────────────────────────────────────────

  getProfileEdits(residentId) {
    return this._getMap(PROFILE_EDITS_KEY, residentId) || {};
  }

  saveProfileField(residentId, fieldPath, value, userId = 'system') {
    const edits = this._getMap(PROFILE_EDITS_KEY, residentId) || {};
    const prev = edits[fieldPath];
    edits[fieldPath] = { value, updatedAt: new Date().toISOString(), updatedBy: userId };
    this._setMap(PROFILE_EDITS_KEY, residentId, edits);
    logDataModification('profile_field', `${residentId}.${fieldPath}`, 'UPDATE', prev, edits[fieldPath], userId);
    return { success: true };
  }
}

export default new ResidentProfileService();
